import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { C, E, RAYON, T, bouton, champ as champStyle } from '../theme';
import { API } from '../api';
import { MARQUE } from '../marque';
import { IconeCorbeille } from '../components/Icones';

// Repères communs à cette page (voir src/theme.js).
const carteSection = {
  background: C.surface, borderRadius: RAYON.lg, padding: E.lg,
  border: `1px solid ${C.bordure}`,
};
const titreSection = { fontSize: '14px', fontWeight: 600, color: C.texte,
                       margin: `0 0 ${E.lg}` };


const inputStyle = champStyle;
const labelStyle = { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#6b7280' };
const btnPrimary = bouton('principal');
const btnSecondary = bouton('secondaire');

// ── Recherche entreprise ───────────────────────────────────────
function ProspectSearchSelect({ prospects, value, onChange }) {
  const sorted = [...prospects].sort((a, b) => a.nom_societe.localeCompare(b.nom_societe, 'fr'));
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = sorted.find(p => String(p.id) === String(value));
  const filtered = sorted.filter(p => p.nom_societe.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          value={open ? query : (selected ? selected.nom_societe : query)}
          onChange={e => { setQuery(e.target.value); onChange(''); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder="Rechercher une entreprise…"
          style={{ ...inputStyle, paddingRight: selected ? '30px' : '14px' }}
        />
        {selected && (
          <button onMouseDown={e => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); }}
            style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '13px', padding: 0 }}>
            ✕
          </button>
        )}
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 300, background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto' }}>
          <div onMouseDown={() => { onChange(''); setQuery(''); setOpen(false); }}
            style={{ padding: '8px 12px', fontSize: '13px', color: '#9ca3af', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
            — Aucune —
          </div>
          {filtered.length === 0
            ? <div style={{ padding: '8px 12px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Aucun résultat</div>
            : filtered.map(p => (
              <div key={p.id} onMouseDown={() => { onChange(String(p.id)); setQuery(''); setOpen(false); }}
                style={{ padding: '8px 12px', fontSize: '13px', color: '#1a2b4a', cursor: 'pointer', fontWeight: String(p.id) === String(value) ? '600' : '400', background: String(p.id) === String(value) ? '#eff6ff' : 'white' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = String(p.id) === String(value) ? '#eff6ff' : 'white'}>
                {p.nom_societe}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── Admin ──────────────────────────────────────────────────────
function Admin({ user }) {
  // Utilisateurs
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nom: '', email: '', mot_de_passe: '', role: 'commercial' });
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [userEdit, setUserEdit] = useState(null);
  const [editForm, setEditForm] = useState({ nom: '', email: '', role: 'commercial' });
  const [editErreur, setEditErreur] = useState('');
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetErreur, setResetErreur] = useState('');

  // Vendeurs CB + prospects
  const [vendeurs, setVendeurs] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [vendeurNom, setVendeurNom] = useState('');
  const [vendeurSucces, setVendeurSucces] = useState('');
  const [vendeurAffaires, setVendeurAffaires] = useState(null);
  const [vendeurAffairesData, setVendeurAffairesData] = useState([]);

  // Édition vendeur enrichie
  const [vendeurEdit, setVendeurEdit] = useState(null); // id du vendeur en cours d'édition
  const [vendeurEditForm, setVendeurEditForm] = useState({ nom: '', telephone: '', adresse: '', entreprise_id: '' });
  const [vendeurSearch, setVendeurSearch] = useState('');

  // Banques partenaires
  const [banques, setBanques] = useState([]);
  const [showBanqueForm, setShowBanqueForm] = useState(false);
  const [editingBanque, setEditingBanque] = useState(null);
  const [banqueForm, setBanqueForm] = useState({ nom: '', email: '', telephone: '', contact: '', notes: '' });
  const [banqueSucces, setBanqueSucces] = useState('');

  // Backup
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupSucces, setBackupSucces] = useState('');
  const [rgpdListe, setRgpdListe] = useState(null);
  const [rgpdLoading, setRgpdLoading] = useState(false);
  const [rgpdMessage, setRgpdMessage] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/utilisateurs`).then(r => setUtilisateurs(r.data)).catch(() => {});
    axios.get(`${API}/vendeurs`).then(r => setVendeurs(r.data)).catch(() => {});
    axios.get(`${API}/prospects`).then(r => setProspects(r.data)).catch(() => {});
    axios.get(`${API}/banques`).then(r => setBanques(r.data)).catch(() => {});
  }, []);

  // ── Handlers utilisateurs ──
  const ouvrirEdition = (u) => { setUserEdit(u); setEditForm({ nom: u.nom, email: u.email, role: u.role }); setEditErreur(''); setResetPwdUser(null); };
  const sauvegarderUtilisateur = () => {
    setEditErreur('');
    axios.put(`${API}/utilisateurs/${userEdit.id}`, editForm, { headers: authHeaders() })
      .then(res => { setUtilisateurs(utilisateurs.map(u => u.id === userEdit.id ? res.data : u)); setUserEdit(null); setSucces('Utilisateur modifié !'); setTimeout(() => setSucces(''), 3000); })
      .catch(err => setEditErreur(err.response?.data?.detail || 'Erreur'));
  };
  const ouvrirResetMdp = (u) => { setResetPwdUser(u); setResetPwd(''); setResetErreur(''); setUserEdit(null); };
  const resetMotDePasse = () => {
    setResetErreur('');
    // Le serveur exige 12 caractères (auth.valider_mot_de_passe) : annoncer 4
    // ici revenait à faire échouer l'admin après coup, sans explication.
    if (!resetPwd || resetPwd.length < 12) return setResetErreur('Minimum 12 caractères');
    axios.put(`${API}/utilisateurs/${resetPwdUser.id}/password`, { nouveau_mot_de_passe: resetPwd }, { headers: authHeaders() })
      .then(() => { setResetPwdUser(null); setSucces(`MDP de ${resetPwdUser.nom} réinitialisé !`); setTimeout(() => setSucces(''), 3000); })
      .catch(err => setResetErreur(err.response?.data?.detail || 'Erreur'));
  };
  const supprimerUtilisateur = (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.nom} ?`)) return;
    axios.delete(`${API}/utilisateurs/${u.id}`, { headers: authHeaders() })
      .then(() => { setUtilisateurs(utilisateurs.filter(x => x.id !== u.id)); setSucces(`Compte supprimé.`); setTimeout(() => setSucces(''), 3000); })
      .catch(err => setErreur(err.response?.data?.detail || 'Erreur'));
  };
  const handleSubmit = () => {
    if (!form.nom || !form.email || !form.mot_de_passe) return setErreur('Remplissez tous les champs');
    axios.post(`${API}/utilisateurs`, form)
      .then(res => { setUtilisateurs([...utilisateurs, res.data]); setShowForm(false); setForm({ nom: '', email: '', mot_de_passe: '', role: 'commercial' }); setErreur(''); setSucces('Compte créé !'); setTimeout(() => setSucces(''), 3000); })
      // Le serveur refuse aussi les mots de passe trop faibles : afficher son
      // message, sinon toute erreur s'affichait comme « Email déjà utilisé ».
      .catch(err => setErreur(err.response?.data?.detail || 'Erreur lors de la création du compte'));
  };

  // ── Handlers vendeurs ──
  const ajouterVendeur = () => {
    if (!vendeurNom.trim()) return;
    axios.post(`${API}/vendeurs`, { nom: vendeurNom.trim() })
      .then(res => { setVendeurs([...vendeurs, res.data]); setVendeurNom(''); setVendeurSucces('Vendeur ajouté !'); setTimeout(() => setVendeurSucces(''), 2000); });
  };

  const ouvrirEditVendeur = (v) => {
    setVendeurEdit(v.id);
    setVendeurEditForm({
      nom: v.nom || '',
      telephone: v.telephone || '',
      adresse: v.adresse || '',
      entreprise_id: v.entreprise_id ? String(v.entreprise_id) : '',
    });
    setVendeurAffaires(null);
  };

  const sauvegarderVendeur = (id) => {
    if (!vendeurEditForm.nom.trim()) return;
    const payload = {
      nom: vendeurEditForm.nom.trim(),
      telephone: vendeurEditForm.telephone || null,
      adresse: vendeurEditForm.adresse || null,
      entreprise_id: vendeurEditForm.entreprise_id ? parseInt(vendeurEditForm.entreprise_id) : null,
    };
    axios.put(`${API}/vendeurs/${id}`, payload)
      .then(res => { setVendeurs(vendeurs.map(v => v.id === id ? res.data : v)); setVendeurEdit(null); setVendeurSucces('Vendeur modifié !'); setTimeout(() => setVendeurSucces(''), 2000); });
  };

  const supprimerVendeur = (id) => {
    axios.delete(`${API}/vendeurs/${id}`)
      .then(() => { setVendeurs(vendeurs.filter(v => v.id !== id)); if (vendeurAffaires === id) setVendeurAffaires(null); setVendeurSucces('Vendeur supprimé.'); setTimeout(() => setVendeurSucces(''), 2000); });
  };

  const voirAffaires = (id) => {
    if (vendeurAffaires === id) { setVendeurAffaires(null); return; }
    axios.get(`${API}/projets?vendeur_id=${id}`).then(res => { setVendeurAffairesData(res.data); setVendeurAffaires(id); });
  };

  const getNomEntreprise = (id) => id ? prospects.find(p => p.id === id)?.nom_societe || null : null;

  // ── Handlers banques ──
  const BANQUE_VIDE = { nom: '', email: '', telephone: '', contact: '', notes: '' };
  const soumettreBanque = () => {
    if (!banqueForm.nom.trim()) return;
    const req = editingBanque
      ? axios.put(`${API}/banques/${editingBanque.id}`, banqueForm)
      : axios.post(`${API}/banques`, banqueForm);
    req.then(res => {
      setBanques(prev => editingBanque
        ? prev.map(b => b.id === editingBanque.id ? res.data : b).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
        : [...prev, res.data].sort((a, b) => a.nom.localeCompare(b.nom, 'fr')));
      setEditingBanque(null); setShowBanqueForm(false); setBanqueForm(BANQUE_VIDE);
      setBanqueSucces(editingBanque ? 'Banque modifiée !' : 'Banque ajoutée !');
      setTimeout(() => setBanqueSucces(''), 2500);
    });
  };
  const supprimerBanque = (id, nom) => {
    if (!window.confirm(`Supprimer "${nom}" ?`)) return;
    axios.delete(`${API}/banques/${id}`).then(() => {
      setBanques(prev => prev.filter(b => b.id !== id));
      setBanqueSucces('Banque supprimée.'); setTimeout(() => setBanqueSucces(''), 2500);
    });
  };
  const formatMoney = (val) => val != null ? Number(val).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €' : '—';

  const lancerBackup = () => {
    setBackupLoading(true); setBackupSucces('');
    axios.get(`${API}/admin/backup`)
      // L'état du chiffrement est affiché : une sauvegarde en clair contient
      // toute la base, l'administrateur doit le voir plutôt que le supposer.
      .then(res => {
        const chiffrement = res.data.chiffre
          ? '🔒 chiffrée'
          : '⚠️ EN CLAIR — définir BACKUP_KEY (voir deploy/maj-cles.sh)';
        setBackupSucces(`✅ Sauvegarde créée : ${res.data.fichier} (${res.data.taille_ko} ko) — ${chiffrement}`);
        setTimeout(() => setBackupSucces(''), 12000);
      })
      .catch(err => { setBackupSucces(`❌ Erreur : ${err.response?.data?.detail || 'échec'}`); setTimeout(() => setBackupSucces(''), 8000); })
      .finally(() => setBackupLoading(false));
  };

  // ── RGPD ──
  const chargerConservation = () => {
    setRgpdLoading(true);
    setRgpdMessage('');
    axios.get(`${API}/admin/rgpd/conservation`)
      .then(res => {
        setRgpdListe(res.data.prospects);
        setRgpdMessage(`${res.data.nombre} prospect(s) au-delà de ${Math.round(res.data.duree_conservation_jours / 365)} an(s) d'inactivité.`);
      })
      .catch(err => setRgpdMessage(`❌ ${err.response?.data?.detail || 'Erreur'}`))
      .finally(() => setRgpdLoading(false));
  };

  const effacerRgpd = async (prospect) => {
    // L'effacement est irréversible et emporte les documents : on montre
    // d'abord ce qu'il détruit, plutôt que de demander un « OK » à l'aveugle.
    let detail = '';
    try {
      const { data } = await axios.get(`${API}/admin/rgpd/prospects/${prospect.id}/inventaire`);
      const lignes = Object.entries(data.elements)
        .filter(([, n]) => n > 0)
        .map(([categorie, n]) => `  • ${n} ${categorie.replace(/_/g, ' ')}`);
      detail = lignes.length ? `\n\nSeront supprimés :\n${lignes.join('\n')}` : '\n\nAucun élément rattaché.';
    } catch {
      detail = '\n\n(Inventaire indisponible.)';
    }
    if (!window.confirm(
      `Effacer définitivement « ${prospect.nom_societe} » ?${detail}\n\n` +
      `Les fichiers téléversés seront supprimés du disque. Cette opération est irréversible.`
    )) return;

    try {
      const { data } = await axios.delete(`${API}/admin/rgpd/prospects/${prospect.id}`);
      setRgpdListe(l => l.filter(p => p.id !== prospect.id));
      setRgpdMessage(`✅ « ${data.nom_societe} » effacée — ${data.fichiers_supprimes} fichier(s) supprimé(s).`);
    } catch (err) {
      setRgpdMessage(`❌ ${err.response?.data?.detail || 'Erreur lors de l\'effacement'}`);
    }
  };

  return (
    <div style={{ fontFamily: T.police, maxWidth: '700px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={T.titre}>Administration</h1>
        <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Gestion des comptes utilisateurs</p>
      </div>

      {succes && <div style={{ background: '#f0fdf4', color: '#059669', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', border: '1px solid #bbf7d0' }}>{succes}</div>}

      {/* ── Utilisateurs ── */}
      <div style={carteSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: '#1a2b4a', fontSize: '16px', fontWeight: '600', margin: 0 }}>Utilisateurs</h2>
          <button onClick={() => setShowForm(!showForm)} style={{ ...btnPrimary, padding: '8px 16px', fontSize: '13px' }}>+ Nouveau compte</button>
        </div>

        {showForm && (
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
            <h3 style={titreSection}>Créer un compte</h3>
            {erreur && <div style={{ background: '#fff0f0', color: '#ef4444', padding: '10px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', border: '1px solid #fecaca' }}>{erreur}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[{ key: 'nom', label: 'Nom complet', type: 'text' }, { key: 'email', label: 'Email', type: 'email' }, { key: 'mot_de_passe', label: 'Mot de passe', type: 'password' }].map(({ key, label, type }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
                  {key === 'mot_de_passe' && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                      12 caractères minimum. Une phrase courte fait un bon mot de passe.
                    </div>
                  )}
                </div>
              ))}
              <div>
                <label style={labelStyle}>Rôle</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inputStyle}>
                  <option value="commercial">Commercial</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={handleSubmit} style={btnPrimary}>Créer le compte</button>
              <button onClick={() => { setShowForm(false); setErreur(''); }} style={btnSecondary}>Annuler</button>
            </div>
          </div>
        )}

        {userEdit && (
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
            <h3 style={titreSection}>Modifier {userEdit.nom}</h3>
            {editErreur && <div style={{ background: '#fff0f0', color: '#ef4444', padding: '10px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', border: '1px solid #fecaca' }}>{editErreur}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[{ key: 'nom', label: 'Nom complet' }, { key: 'email', label: 'Email' }].map(({ key, label }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input value={editForm[key]} onChange={e => setEditForm({ ...editForm, [key]: e.target.value })} style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>Rôle</label>
                <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} style={inputStyle}>
                  <option value="commercial">Commercial</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={sauvegarderUtilisateur} style={btnPrimary}>Enregistrer</button>
              <button onClick={() => setUserEdit(null)} style={btnSecondary}>Annuler</button>
            </div>
          </div>
        )}

        {resetPwdUser && (
          <div style={{ background: '#fff7ed', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fed7aa' }}>
            <h3 style={titreSection}>Réinitialiser le MDP de {resetPwdUser.nom}</h3>
            {resetErreur && <div style={{ background: '#fff0f0', color: '#ef4444', padding: '10px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', border: '1px solid #fecaca' }}>{resetErreur}</div>}
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Nouveau mot de passe provisoire</label>
              <input type="password" value={resetPwd} onChange={e => setResetPwd(e.target.value)} style={inputStyle} autoFocus />
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                12 caractères minimum. Une phrase courte fait un bon mot de passe.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button onClick={resetMotDePasse} style={{ ...btnPrimary, background: '#d97706' }}>Réinitialiser</button>
              <button onClick={() => setResetPwdUser(null)} style={btnSecondary}>Annuler</button>
            </div>
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {['Nom', 'Email', 'Rôle', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {utilisateurs.filter(u => u.role === 'admin' || u.role === 'commercial').map((u, i) => (
              <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '14px', fontSize: '14px', fontWeight: '500', color: '#1a2b4a' }}>{u.nom}</td>
                <td style={{ padding: '14px', fontSize: '14px', color: '#374151' }}>{u.email}</td>
                <td style={{ padding: '14px' }}>
                  <span style={{ background: u.role === 'admin' ? '#ede9fe' : '#dbeafe', color: u.role === 'admin' ? '#7c3aed' : '#1d4ed8', padding: '4px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', border: u.role === 'admin' ? '1px solid #c4b5fd' : '1px solid #bfdbfe' }}>
                    {u.role === 'admin' ? 'Admin' : 'Commercial'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => ouvrirEdition(u)} style={{ background: 'white', color: '#374151', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Modifier</button>
                    <button onClick={() => ouvrirResetMdp(u)} style={{ background: 'white', color: '#d97706', padding: '5px 10px', border: '1px solid #fed7aa', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Réinitialiser</button>
                    <button title="Supprimer" onClick={() => supprimerUtilisateur(u)} style={{ background: 'white', color: '#ef4444', padding: '5px 10px', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}><IconeCorbeille taille={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Vendeurs du cabinet ── */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: '24px' }}>
        <h2 style={titreSection}>Vendeurs {MARQUE.nom}</h2>

        {vendeurSucces && (
          <div style={{ background: '#f0fdf4', color: '#059669', padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', border: '1px solid #bbf7d0' }}>{vendeurSucces}</div>
        )}

        {/* Ajout rapide */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
          <input value={vendeurNom} onChange={e => setVendeurNom(e.target.value)} onKeyDown={e => e.key === 'Enter' && ajouterVendeur()} placeholder="Nom du vendeur" style={{ ...inputStyle, flex: 1 }} />
          <button onClick={ajouterVendeur} style={{ ...btnPrimary, padding: '10px 20px', fontSize: '13px', whiteSpace: 'nowrap' }}>+ Ajouter</button>
        </div>

        {/* Recherche */}
        {vendeurs.length > 0 && (
          <input
            value={vendeurSearch}
            onChange={e => setVendeurSearch(e.target.value)}
            placeholder="🔍 Rechercher un vendeur…"
            style={{ ...inputStyle, marginBottom: '16px', background: '#f8fafc' }}
          />
        )}

        {vendeurs.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>Aucun vendeur. Ajoutez les commerciaux partenaires pour suivre leurs commissions.</p>
        ) : (
          <div style={{ maxHeight: '420px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '4px' }}>
            {[...vendeurs]
              .sort((a, b) => a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }))
              .filter(v => v.nom.toLowerCase().includes(vendeurSearch.toLowerCase()))
              .map(v => {
              const nomEntreprise = getNomEntreprise(v.entreprise_id);
              const isEditing = vendeurEdit === v.id;
              return (
                <div key={v.id}>
                  {isEditing ? (
                    /* Formulaire d'édition enrichi */
                    <div key="edit" style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '16px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a2b4a', marginBottom: '12px' }}>Modifier — {v.nom}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                        <div>
                          <label style={labelStyle}>Nom *</label>
                          <input value={vendeurEditForm.nom} onChange={e => setVendeurEditForm(f => ({ ...f, nom: e.target.value }))} style={inputStyle} autoFocus />
                        </div>
                        <div>
                          <label style={labelStyle}>Téléphone</label>
                          <input value={vendeurEditForm.telephone} onChange={e => setVendeurEditForm(f => ({ ...f, telephone: e.target.value }))} placeholder="06 12 34 56 78" style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={labelStyle}>Adresse</label>
                          <input value={vendeurEditForm.adresse} onChange={e => setVendeurEditForm(f => ({ ...f, adresse: e.target.value }))} placeholder="12 rue de la Paix, 75001 Paris" style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={labelStyle}>Entreprise associée</label>
                          <ProspectSearchSelect
                            prospects={prospects}
                            value={vendeurEditForm.entreprise_id}
                            onChange={val => setVendeurEditForm(f => ({ ...f, entreprise_id: val }))}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => sauvegarderVendeur(v.id)} style={{ background: '#059669', color: 'white', padding: '8px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Sauvegarder</button>
                        <button onClick={() => setVendeurEdit(null)} style={{ background: 'white', color: '#6b7280', padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    /* Carte vendeur */
                    <div key="card" style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a2b4a', marginBottom: '3px' }}>{v.nom}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '13px', color: '#6b7280' }}>
                            {v.telephone && <span>{v.telephone}</span>}
                            {v.adresse && <span>{v.adresse}</span>}
                            {nomEntreprise && <span style={{ color: '#7c3aed', fontWeight: '500' }}>{nomEntreprise}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                          <button onClick={() => voirAffaires(v.id)}
                            style={{ background: vendeurAffaires === v.id ? '#1a2b4a' : 'white', color: vendeurAffaires === v.id ? 'white' : '#374151', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                            {vendeurAffaires === v.id ? '▲ Masquer' : '📋 Affaires'}
                          </button>
                          <button onClick={() => ouvrirEditVendeur(v)} style={{ background: 'white', color: '#374151', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Modifier</button>
                          <button title="Supprimer" onClick={() => supprimerVendeur(v.id)} style={{ background: 'white', color: '#ef4444', padding: '5px 10px', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}><IconeCorbeille taille={15} /></button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Affaires du vendeur */}
                  {vendeurAffaires === v.id && !isEditing && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginTop: '4px' }}>
                      {vendeurAffairesData.length === 0 ? (
                        <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>Aucune affaire pour ce vendeur.</p>
                      ) : (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>{vendeurAffairesData.length} affaire(s)</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: '#059669' }}>Cumul : {formatMoney(vendeurAffairesData.reduce((s, p) => s + (p.commission_vendeur || 0), 0))}</span>
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                {['Produit', 'Type financement', 'Prix HT', 'Commission'].map(h => (
                                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {vendeurAffairesData.map(p => (
                                <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '8px 10px', color: '#1a2b4a', fontWeight: '500' }}>{p.nom_produit || p.materiel || '—'}</td>
                                  <td style={{ padding: '8px 10px', color: '#374151' }}>{p.type_financement || '—'}</td>
                                  <td style={{ padding: '8px 10px', color: '#374151' }}>{formatMoney(p.prix_produit ?? p.valeur_ht)}</td>
                                  <td style={{ padding: '8px 10px', fontWeight: '600', color: '#059669' }}>{formatMoney(p.commission_vendeur)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Banques partenaires ── */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ ...titreSection, marginBottom: E.xs }}>Banques partenaires</h2>
            <p style={{ color: '#9ca3af', fontSize: '13px', margin: 0 }}>Utilisées pour l'auto-remplissage lors de l'envoi des dossiers</p>
          </div>
          {!showBanqueForm && !editingBanque && (
            <button onClick={() => { setShowBanqueForm(true); setBanqueForm(BANQUE_VIDE); }} style={{ ...btnPrimary, padding: '8px 16px', fontSize: '13px' }}>+ Ajouter</button>
          )}
        </div>

        {banqueSucces && (
          <div style={{ background: '#f0fdf4', color: '#059669', padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', border: '1px solid #bbf7d0' }}>{banqueSucces}</div>
        )}

        {(showBanqueForm || editingBanque) && (
          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a2b4a', marginBottom: '12px' }}>
              {editingBanque ? `✏️ Modifier — ${editingBanque.nom}` : '+ Nouvelle banque'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { key: 'nom', label: 'Nom *', placeholder: 'BNP Paribas Leasing' },
                { key: 'email', label: 'Email', placeholder: 'contact@banque.fr' },
                { key: 'telephone', label: 'Téléphone', placeholder: '01 23 45 67 89' },
                { key: 'contact', label: 'Interlocuteur', placeholder: 'Service commercial' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input value={banqueForm[key]} onChange={e => setBanqueForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder} style={inputStyle} />
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <input value={banqueForm.notes} onChange={e => setBanqueForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Conditions, spécificités…" style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={soumettreBanque} style={{ background: '#1a2b4a', color: 'white', padding: '8px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }} disabled={!banqueForm.nom.trim()}>Enregistrer
              </button>
              <button onClick={() => { setShowBanqueForm(false); setEditingBanque(null); }} style={{ background: 'white', color: '#6b7280', padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {banques.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '14px' }}>Aucune banque partenaire. Ajoutez-en pour pouvoir leur envoyer des dossiers.</div>
        ) : (
          <div style={{ maxHeight: '420px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px' }}>
            {banques.map(b => (
              <div key={b.id} style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a2b4a', marginBottom: '3px' }}>{b.nom}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '13px', color: '#6b7280' }}>
                      {b.email && <span>✉️ {b.email}</span>}
                      {b.telephone && <span>{b.telephone}</span>}
                      {b.contact && <span>{b.contact}</span>}
                      {b.notes && <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>— {b.notes}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button onClick={() => { setEditingBanque(b); setShowBanqueForm(false); setBanqueForm({ nom: b.nom, email: b.email || '', telephone: b.telephone || '', contact: b.contact || '', notes: b.notes || '' }); }}
                      style={{ background: 'white', color: '#374151', padding: '5px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Modifier</button>
                    <button title="Supprimer" onClick={() => supprimerBanque(b.id, b.nom)}
                      style={{ background: 'white', color: '#ef4444', padding: '5px 10px', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}><IconeCorbeille taille={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Sauvegarde BDD ── */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: '24px' }}>
        <h2 style={titreSection}>Sauvegarde de la base de données</h2>
        <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 16px' }}>Génère un dump PostgreSQL complet de la base <code>crm_cesemat</code> dans le dossier <code>backend/backups/</code>. Le fichier est chiffré (AES-256-GCM) si <code>BACKUP_KEY</code> est configurée, et conservé 30 jours.</p>
        {backupSucces && (
          <div style={{ background: backupSucces.startsWith('❌') ? '#fff0f0' : '#f0fdf4', color: backupSucces.startsWith('❌') ? '#ef4444' : '#059669', padding: '12px 16px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', border: `1px solid ${backupSucces.startsWith('❌') ? '#fecaca' : '#bbf7d0'}` }}>
            {backupSucces}
          </div>
        )}
        <button onClick={lancerBackup} disabled={backupLoading} style={{ ...btnPrimary, background: backupLoading ? '#6b7280' : '#1a2b4a', cursor: backupLoading ? 'not-allowed' : 'pointer' }}>
          {backupLoading ? '⏳ Sauvegarde en cours...' : '💾 Sauvegarder la base de données'}
        </button>
      </div>

      {/* ── RGPD ── */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: '24px' }}>
        <h2 style={titreSection}>RGPD — conservation et effacement</h2>
        <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 16px' }}>
          Les prospects sans activité depuis 3 ans n'ont plus à être conservés (recommandation CNIL).
          Rien n'est supprimé automatiquement : la décision reste ici. Les clients et apporteurs ne
          sont pas listés — leur conservation répond à des obligations comptables et assurantielles.
        </p>
        {rgpdMessage && (
          <div style={{ background: rgpdMessage.startsWith('❌') ? '#fff0f0' : '#f0fdf4', color: rgpdMessage.startsWith('❌') ? '#ef4444' : '#059669', padding: '12px 16px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', border: `1px solid ${rgpdMessage.startsWith('❌') ? '#fecaca' : '#bbf7d0'}` }}>
            {rgpdMessage}
          </div>
        )}
        <button onClick={chargerConservation} disabled={rgpdLoading}
          style={{ ...btnPrimary, background: rgpdLoading ? '#6b7280' : '#1a2b4a', cursor: rgpdLoading ? 'not-allowed' : 'pointer' }}>
          {rgpdLoading ? '⏳ Analyse en cours...' : '🔍 Lister les prospects à purger'}
        </button>

        {rgpdListe !== null && (
          <div style={{ marginTop: '18px' }}>
            {rgpdListe.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '13px' }}>Aucun prospect ne dépasse la durée de conservation.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '12px', color: '#6b7280' }}>SOCIÉTÉ</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '12px', color: '#6b7280' }}>INACTIVITÉ</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '12px', color: '#6b7280' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {rgpdListe.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontSize: '13px' }}>{p.nom_societe}</td>
                      <td style={{ padding: '8px', fontSize: '13px', color: '#6b7280' }}>
                        {Math.floor(p.jours_inactivite / 365)} an(s)
                      </td>
                      <td style={{ padding: '8px' }}>
                        <button onClick={() => effacerRgpd(p)}
                          style={{ background: 'white', color: '#ef4444', padding: '5px 10px', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Effacer définitivement
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
